import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

// FIX: backend è su 3001. Ignoriamo REACT_APP_API_BASE per evitare casini di config locali.
const API_BASE = "http://localhost:3001";

// ---------- helpers ----------
const WD_ORDER = [1, 2, 3, 4, 5, 6, 7]; // 1=Mon..7=Sun

function toISO(d) {
  if (typeof d === "string") return d;
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, "0");
  const da = String(d.getUTCDate()).padStart(2, "0");
  return `${y}-${m}-${da}`;
}

function parseISO(iso) {
  const [y, m, d] = String(iso).split("-").map((x) => parseInt(x, 10));
  return new Date(Date.UTC(y, (m || 1) - 1, d || 1));
}

function weekdayMon1Sun7(dateUTC) {
  const js = dateUTC.getUTCDay();
  return js === 0 ? 7 : js;
}

function addDaysUTC(dateUTC, days) {
  const d = new Date(dateUTC.getTime());
  d.setUTCDate(d.getUTCDate() + days);
  return d;
}

function nextWorkingDate(iso, workingDays, holidaySet) {
  let d = parseISO(iso);
  while (true) {
    d = addDaysUTC(d, 1);
    const wd = weekdayMon1Sun7(d);
    const isWorking = workingDays.includes(wd);
    const isHoliday = holidaySet.has(toISO(d));
    if (isWorking && !isHoliday) return toISO(d);
  }
}

function clampInt(v, min, max) {
  const n = parseInt(v, 10);
  if (!Number.isFinite(n)) return min;
  return Math.max(min, Math.min(max, n));
}

function uniqISOList(arr) {
  const out = [];
  const s = new Set();
  for (const x of arr || []) {
    const v = String(x || "").trim();
    if (!/^\d{4}-\d{2}-\d{2}$/.test(v)) continue;
    if (s.has(v)) continue;
    s.add(v);
    out.push(v);
  }
  return out.sort();
}

// Anonymous Gregorian algorithm (Meeus/Jones/Butcher) for Easter Sunday
function easterSundayISO(year) {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31); // 3=Mar, 4=Apr
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  const mm = String(month).padStart(2, "0");
  const dd = String(day).padStart(2, "0");
  return `${year}-${mm}-${dd}`;
}

function italyNationalHolidaysISO(year) {
  // Fixed-date national holidays + Easter Monday
  const fixed = [
    `${year}-01-01`, // Capodanno
    `${year}-01-06`, // Epifania
    `${year}-04-25`, // Liberazione
    `${year}-05-01`, // Lavoro
    `${year}-06-02`, // Repubblica
    `${year}-08-15`, // Ferragosto
    `${year}-11-01`, // Ognissanti
    `${year}-12-08`, // Immacolata
    `${year}-12-25`, // Natale
    `${year}-12-26`, // Santo Stefano
  ];

  // Easter Monday
  const easterSun = easterSundayISO(year);
  const easterMon = toISO(addDaysUTC(parseISO(easterSun), 1));
  return [...fixed, easterMon];
}

// ---------- presets ----------
const BUILTIN_PRESETS = [
  {
    id: "standard_tv_post_v1",
    name: "Standard TV Post (Advanced)",
    programType: "daily_5_7",
    lineTypes: [
      { typeKey: "ingest", label: "Ingest", count: 1, perEpisode: 1, daysPerEpisode: 1, blocking: true },
      { typeKey: "logger", label: "Logger", count: 1, perEpisode: 1, daysPerEpisode: 1, blocking: true },
      { typeKey: "taglio", label: "Taglio contenuti", count: 1, perEpisode: 1, daysPerEpisode: 1, blocking: true },
      { typeKey: "montaggio", label: "Montaggio", count: 3, perEpisode: 2, daysPerEpisode: 4, blocking: true },
      { typeKey: "final_prep", label: "Assemblaggio", count: 1, perEpisode: 1, daysPerEpisode: 1, blocking: true },
      { typeKey: "vis_autore", label: "Visione Autore", count: 1, perEpisode: 1, daysPerEpisode: 1, blocking: true },
      { typeKey: "mod_autore", label: "Modifiche post-Autore", count: 1, perEpisode: 1, daysPerEpisode: 1, blocking: true },
      { typeKey: "vis_curatore", label: "Visione Curatore", count: 1, perEpisode: 1, daysPerEpisode: 1, blocking: true },
      { typeKey: "mod_curatore", label: "Modifiche post-Curatore", count: 1, perEpisode: 1, daysPerEpisode: 1, blocking: true },
      { typeKey: "vis_rete", label: "Visione Rete", count: 1, perEpisode: 1, daysPerEpisode: 1, blocking: true },
      { typeKey: "mod_rete", label: "Modifiche post-Rete", count: 1, perEpisode: 1, daysPerEpisode: 2, blocking: true },
      { typeKey: "audio", label: "Audio", count: 1, perEpisode: 1, daysPerEpisode: 2, blocking: true, depTypeKey: 'mod_rete' },
      { typeKey: "color", label: "Color", count: 1, perEpisode: 1, daysPerEpisode: 2, blocking: true, depTypeKey: 'mod_rete' },
      { typeKey: "master", label: "Consegna Master", count: 1, perEpisode: 1, daysPerEpisode: 0, blocking: true },
    ],
  },
];

const LS_KEY = "maestranze_postplan_presets_v1";

function loadUserPresets() {
  try {
    const raw = localStorage.getItem(LS_KEY);
    const arr = raw ? JSON.parse(raw) : [];
    return Array.isArray(arr) ? arr : [];
  } catch {
    return [];
  }
}

function saveUserPresets(presets) {
  localStorage.setItem(LS_KEY, JSON.stringify(presets));
}

// ---------- main ----------
export default function PostPlanWizardPage() {
  const { id } = useParams();
  const planId = Number(id);
  const nav = useNavigate();

  // setup
  const [programType, setProgramType] = useState("daily_5_7");
  const [projectStartDate, setProjectStartDate] = useState("2026-02-01");
  const [episodesCount, setEpisodesCount] = useState(20);
  const [txStartDate, setTxStartDate] = useState("2026-02-02");

  // calendar
  const [workingDays, setWorkingDays] = useState([1, 2, 3, 4, 5]);
  const [holidaysText, setHolidaysText] = useState("");
  const [holidayPick, setHolidayPick] = useState("");
  const [includeNationalIT, setIncludeNationalIT] = useState(true);

  // episodes grid
  const [episodes, setEpisodes] = useState(() =>
    Array.from({ length: 20 }).map((_, i) => ({
      episodeNo: i + 1,
      rxDate: "",
      arrivalDate: "",
      txDate: "",
    }))
  );

  // phases (lineTypes)
  const [lineTypes, setLineTypes] = useState(BUILTIN_PRESETS[0].lineTypes);

  // presets
  const [userPresets, setUserPresets] = useState(() => loadUserPresets());
  const allPresets = useMemo(() => [...BUILTIN_PRESETS, ...userPresets], [userPresets]);
  const [selectedPresetId, setSelectedPresetId] = useState(BUILTIN_PRESETS[0].id);
  const [newPresetName, setNewPresetName] = useState("");

  // submit
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  // plan meta (name)
  const [planName, setPlanName] = useState("");
  const holidays = useMemo(() => {
    return holidaysText
      .split(/\r?\n|,|;|\s+/)
      .map((s) => s.trim())
      .filter(Boolean)
      .filter((s) => /^\d{4}-\d{2}-\d{2}$/.test(s));
  }, [holidaysText]);
  

  const yearsInPlan = useMemo(() => {
    const ys = new Set();
    const grab = (iso) => {
      if (!iso || !/^\d{4}-\d{2}-\d{2}$/.test(String(iso))) return;
      ys.add(parseInt(String(iso).slice(0, 4), 10));
    };
    grab(projectStartDate);
    grab(txStartDate);
    for (const e of episodes) {
      grab(e.rxDate);
      grab(e.arrivalDate);
      grab(e.txDate);
    }
    if (!ys.size) ys.add(new Date().getUTCFullYear());
    return Array.from(ys).filter(Number.isFinite).sort();
  }, [projectStartDate, txStartDate, episodes]);

  const nationalIT = useMemo(() => {
    if (!includeNationalIT) return [];
    const all = yearsInPlan.flatMap((y) => italyNationalHolidaysISO(y));
    return uniqISOList(all);
  }, [includeNationalIT, yearsInPlan]);

  const holidaysEffective = useMemo(() => {
    // union user-entered + national fixed list
    return uniqISOList([...(holidays || []), ...(nationalIT || [])]);
  }, [holidays, nationalIT]);
const holidaySet = useMemo(() => new Set(holidaysEffective), [holidaysEffective]);
  // load plan name (so wizard can rename without going to timeline)
  useEffect(() => {
    if (!Number.isFinite(planId)) return;
    (async () => {
      try {
        const r = await fetch(`${API_BASE}/api/postplans/${planId}`, {
          headers: { Accept: "application/json" },
        });
        if (!r.ok) return;
        const j = await r.json();
        setPlanName(String(j?.name || ""));
        // Carica fasi esistenti dal DB per non sovrascrivere con default
        if (Array.isArray(j?.phases) && j.phases.length > 0) {
          const loadedLineTypes = j.phases.map(p => ({
            typeKey: p.type_key || p.typeKey || '',
            label: p.name || '',
            count: p.default_lines || 1,
            perEpisode: p.per_episode || 1,
            daysPerEpisode: Number(p.base_duration_days ?? p.baseDurationDays ?? 1),
            blocking: p.blocking !== false,
            depPhaseSort: p.dep_phase_sort != null ? Number(p.dep_phase_sort) : null,
            depType: p.dep_type || 'end',
            depOffsetDays: Number(p.dep_offset_days) || 0,
          }));
          setLineTypes(loadedLineTypes);
        }
      } catch {}
    })();
  }, [planId]);
  // apply programType -> workingDays defaults
  useEffect(() => {
    if (programType === "daily_5_7") setWorkingDays([1, 2, 3, 4, 5]);
    else if (programType === "daily_6_7") setWorkingDays([1, 2, 3, 4, 5, 6]);
    else if (programType === "daily_7_7") setWorkingDays([1, 2, 3, 4, 5, 6, 7]);
    // weekly: keep current (often 5/7)
  }, [programType]);

  // keep episodes array sized
  useEffect(() => {
    setEpisodes((prev) => {
      const next = Array.from({ length: episodesCount }).map((_, i) => {
        const old = prev[i];
        return old
          ? { ...old, episodeNo: i + 1 }
          : { episodeNo: i + 1, rxDate: "", arrivalDate: "", txDate: "" };
      });
      return next;
    });
  }, [episodesCount]);

  function applyPreset(presetId) {
    const p = allPresets.find((x) => x.id === presetId);
    if (!p) return;
    setSelectedPresetId(p.id);
    if (p.programType) setProgramType(p.programType);
    if (Array.isArray(p.lineTypes)) setLineTypes(p.lineTypes);
  }

  function updateEpisode(i, patch) {
    setEpisodes((prev) => prev.map((e, idx) => (idx === i ? { ...e, ...patch } : e)));
  }

  function fillTxSeries() {
    if (!txStartDate) return;
    setEpisodes((prev) => {
      const next = prev.map((e) => ({ ...e }));
      let cur = txStartDate;
      for (let i = 0; i < next.length; i++) {
        next[i].txDate = cur;
        if (i < next.length - 1) {
          if (programType === "weekly") {
            cur = toISO(addDaysUTC(parseISO(cur), 7));
          } else {
            cur = nextWorkingDate(cur, workingDays, holidaySet);
          }
        }
      }
      return next;
    });
  }

  function addHolidayFromPicker() {
    if (!holidayPick) return;
    const merged = uniqISOList([...(holidays || []), holidayPick]);
    setHolidaysText(merged.join("\n"));
    setHolidayPick("");
  }

  function clearLocalHolidays() {
    setHolidaysText("");
  }

  function fillArrivalFromRx() {
    setEpisodes((prev) => prev.map((e) => ({ ...e, arrivalDate: e.arrivalDate || e.rxDate || "" })));
  }

  function toggleWorkingDay(dayNo) {
    setWorkingDays((prev) => {
      const s = new Set(prev);
      if (s.has(dayNo)) s.delete(dayNo);
      else s.add(dayNo);
      return WD_ORDER.filter((d) => s.has(d));
    });
  }

  function saveAsPreset() {
    const name = newPresetName.trim();
    if (!name) return;
    const id = `user_${Date.now()}`;
    const p = {
      id,
      name,
      programType,
      lineTypes,
    };
    const next = [...userPresets, p];
    setUserPresets(next);
    saveUserPresets(next);
    setNewPresetName("");
    setSelectedPresetId(id);
  }

  function addLineType() {
    const id = Date.now();
    setLineTypes((prev) => [
      ...prev,
      {
        typeKey: `custom_${id}`,
        label: "Nuova fase",
        count: 1,
        perEpisode: 1,
        daysPerEpisode: 1,
        blocking: true,
      },
    ]);
  }

  function deleteLineType(idx) {
    setLineTypes((prev) => prev.filter((_, i) => i !== idx));
  }

  function moveLineType(idx, dir) {
    setLineTypes((prev) => {
      const next = [...prev];
      const j = idx + dir;
      if (j < 0 || j >= next.length) return prev;
      const tmp = next[idx];
      next[idx] = next[j];
      next[j] = tmp;
      return next;
    });
  }

  function deletePreset(presetId) {
    // only user presets
    const next = userPresets.filter((p) => p.id !== presetId);
    setUserPresets(next);
    saveUserPresets(next);
    setSelectedPresetId(BUILTIN_PRESETS[0].id);
  }
  async function savePlanName(nextName) {
    const name = String(nextName || "").trim();
    if (!name) return;
    try {
      await fetch(`${API_BASE}/api/postplans/${planId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ name }),
      });
    } catch {}
  }
  function validate() {
    if (!Number.isFinite(planId)) return "ID piano non valido";
    if (!projectStartDate) return "Data inizio progetto obbligatoria";
    if (episodesCount < 1) return "Numero episodi deve essere >= 1";
    if (workingDays.length < 1) return "Seleziona almeno 1 working day";
    if (!Array.isArray(lineTypes) || lineTypes.length < 1) return "Linee/Fasi mancanti";
    for (const e of episodes) {
      if (!e.txDate) return `TX mancante episodio ${e.episodeNo}`;
      if (!e.arrivalDate) return `Arrivo girato mancante episodio ${e.episodeNo}`;
    }
    for (const lt of lineTypes) {
      if (!lt.typeKey || !lt.label) return "Tipo/Label fase mancante";
      if (lt.count < 1) return `Count invalid per ${lt.label}`;
      if (lt.perEpisode < 1 || lt.perEpisode > lt.count) return `perEpisode invalid per ${lt.label}`;
      if (lt.daysPerEpisode < 0) return `daysPerEpisode invalid per ${lt.label}`;
    }
    return "";
  }

  async function submit() {
    const v = validate();
    if (v) {
      setErr(v);
      return;
    }

    setBusy(true);
    setErr("");
    try {
      const payload = {
        programType,
        projectStartDate,
        workingDays,
        // holidaysEffective includes national IT (if enabled)
        holidays: holidaysEffective,
        episodesCount,
        episodes: episodes.map((e) => ({
          episodeNo: e.episodeNo,
          rxDate: e.rxDate || null,
          arrivalDate: e.arrivalDate,
          txDate: e.txDate,
        })),
        lineTypes,
      };

      const resp = await fetch(`${API_BASE}/api/postplans/${planId}/wizard`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify(payload),
      });
      const json = await resp.json();
      if (!resp.ok) throw new Error(json?.error || `HTTP ${resp.status}`);

      nav(`/postplans/${planId}`);
    } catch (e) {
      setErr(e?.message || String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={{ padding: 16, maxWidth: 1200, margin: "0 auto" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
        <h2 style={{ margin: 0 }}>Wizard PostPlan (Advanced)</h2>
        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={() => nav(`/postplans/${planId}`)} disabled={busy}>
            Annulla
          </button>
          <button onClick={submit} disabled={busy} style={{ fontWeight: 800 }}>
            {busy ? "Genero..." : "Genera piano"}
          </button>
        </div>
      </div>

      {err ? (
        <div style={{ marginTop: 12, padding: 10, border: "1px solid #f3c", borderRadius: 8, color: "#900" }}>
          {err}
        </div>
      ) : null}

      {/* PRESETS */}
      <div style={{ marginTop: 14, padding: 12, border: "1px solid #ddd", borderRadius: 8 }}>
        <h3 style={{ margin: "0 0 10px 0" }}>Preset</h3>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
          <select
            value={selectedPresetId}
            onChange={(e) => applyPreset(e.target.value)}
            style={{ minWidth: 320 }}
          >
            {allPresets.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
          {selectedPresetId.startsWith("user_") ? (
            <button onClick={() => deletePreset(selectedPresetId)} disabled={busy}>
              Elimina preset
            </button>
          ) : null}
        </div>

        <div style={{ marginTop: 10, display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
          <input
            value={newPresetName}
            onChange={(e) => setNewPresetName(e.target.value)}
            placeholder="Nome nuovo preset..."
            style={{ minWidth: 260 }}
          />
          <button onClick={saveAsPreset} disabled={!newPresetName.trim() || busy}>
            Salva come preset
          </button>
        </div>
      </div>

      {/* SETUP */}
      <div style={{ marginTop: 14, padding: 12, border: "1px solid #ddd", borderRadius: 8 }}>
        <h3 style={{ margin: "0 0 10px 0" }}>Setup</h3>
        <div style={{ display: "grid", gridTemplateColumns: "220px 1fr", gap: 10, maxWidth: 700 }}>
                    <label>Nome versione piano</label>
          <input
            value={planName}
            onChange={(e) => setPlanName(e.target.value)}
            onBlur={() => savePlanName(planName)}
            placeholder='Es. "v1", "v2 consegna stretta"...'
          />
          <label>Tipo programma</label>
          <select value={programType} onChange={(e) => setProgramType(e.target.value)}>
            <option value="daily_5_7">Quotidiano 5/7</option>
            <option value="daily_6_7">Quotidiano 6/7</option>
            <option value="daily_7_7">Quotidiano 7/7</option>
            <option value="weekly">Weekly</option>
          </select>

          <label>Inizio progetto</label>
          <input type="date" value={projectStartDate} onChange={(e) => setProjectStartDate(e.target.value)} />

          <label>Episodi</label>
          <input
            type="number"
            value={episodesCount}
            min={1}
            max={999}
            onChange={(e) => setEpisodesCount(clampInt(e.target.value, 1, 999))}
          />

          <label>TX start (per compilazione serie)</label>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <input type="date" value={txStartDate} onChange={(e) => setTxStartDate(e.target.value)} />
            <button onClick={fillTxSeries} type="button">Compila TX in serie</button>
          </div>
        </div>

        <div style={{ marginTop: 12 }}>
          <div style={{ fontWeight: 800, marginBottom: 6 }}>Turni (working days)</div>
          {["Mon","Tue","Wed","Thu","Fri","Sat","Sun"].map((lbl, idx) => {
            const dayNo = idx + 1;
            return (
              <label key={lbl} style={{ marginRight: 10 }}>
                <input
                  type="checkbox"
                  checked={workingDays.includes(dayNo)}
                  onChange={() => toggleWorkingDay(dayNo)}
                />{" "}
                {lbl}
              </label>
            );
          })}
        </div>

        <div style={{ marginTop: 12, display: "grid", gridTemplateColumns: "220px 1fr", gap: 10, maxWidth: 700 }}>
          <label>Festivi (YYYY-MM-DD)</label>
          <div>
            <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
              <input type="date" value={holidayPick} onChange={(e) => setHolidayPick(e.target.value)} />
              <button
                type="button"
                onClick={() => {
                  if (!holidayPick) return;
                  const merged = uniqISOList([...(holidays || []), holidayPick]);
                  setHolidaysText(merged.join("\n"));
                  setHolidayPick("");
                }}
              >
                Aggiungi
              </button>
              <label style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <input
                  type="checkbox"
                  checked={includeNationalIT}
                  onChange={(e) => setIncludeNationalIT(e.target.checked)}
                />
                Includi festivi nazionali IT (fissi + Pasquetta)
              </label>
            </div>

            <textarea
              value={holidaysText}
              onChange={(e) => setHolidaysText(e.target.value)}
              rows={4}
              placeholder="Inserisci i festivi locali/azienda (uno per riga, oppure separati da virgola)"
              style={{ width: "100%", marginTop: 8 }}
            />
            <div style={{ marginTop: 6, fontSize: 12, color: "#666" }}>
  I festivi nazionali sono già conteggiati se “Includi festivi nazionali IT” è attivo.
  Qui puoi aggiungere eventuali festivi locali/aziendali (es. santo patrono, chiusure interne).
  Puoi anche modificarli in seguito: riapri il wizard e rigenera il piano.
</div>
            {holidaysEffective.length ? (
              <div style={{ marginTop: 6, fontSize: 12, color: "#333" }}>
                <b>Totale festivi considerati:</b> {holidaysEffective.length}
              </div>
            ) : null}
          </div>
        </div>
      </div>

      {/* EPISODES */}
      <div style={{ marginTop: 14, padding: 12, border: "1px solid #ddd", borderRadius: 8 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
          <h3 style={{ margin: 0 }}>Episodi (RX / Arrivo girato / TX)</h3>
          <button onClick={fillArrivalFromRx} type="button">Copia RX → Arrivo (se vuoto)</button>
        </div>

        <div style={{ marginTop: 10, overflowX: "auto" }}>
          <table style={{ borderCollapse: "collapse", width: "100%" }}>
            <thead>
              <tr>
                <th style={{ textAlign: "left", padding: 6, borderBottom: "1px solid #ddd" }}>Ep</th>
                <th style={{ textAlign: "left", padding: 6, borderBottom: "1px solid #ddd" }}>RX (registrazione)</th>
                <th style={{ textAlign: "left", padding: 6, borderBottom: "1px solid #ddd" }}>Arrivo girato (comanda)</th>
                <th style={{ textAlign: "left", padding: 6, borderBottom: "1px solid #ddd" }}>TX</th>
              </tr>
            </thead>
            <tbody>
              {episodes.map((e, i) => (
                <tr key={e.episodeNo}>
                  <td style={{ padding: 6, borderBottom: "1px solid #eee" }}>{e.episodeNo}</td>
                  <td style={{ padding: 6, borderBottom: "1px solid #eee" }}>
                    <input type="date" value={e.rxDate} onChange={(ev) => updateEpisode(i, { rxDate: ev.target.value })} />
                  </td>
                  <td style={{ padding: 6, borderBottom: "1px solid #eee" }}>
                    <input
                      type="date"
                      value={e.arrivalDate}
                      onChange={(ev) => updateEpisode(i, { arrivalDate: ev.target.value })}
                      style={{ border: e.arrivalDate ? "1px solid #ccc" : "1px solid #c00" }}
                    />
                  </td>
                  <td style={{ padding: 6, borderBottom: "1px solid #eee" }}>
                    <input
                      type="date"
                      value={e.txDate}
                      onChange={(ev) => updateEpisode(i, { txDate: ev.target.value })}
                      style={{ border: e.txDate ? "1px solid #ccc" : "1px solid #c00" }}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* PHASES */}
      <div style={{ marginTop: 14, padding: 12, border: "1px solid #ddd", borderRadius: 8 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
          <h3 style={{ margin: 0 }}>Linee/Fasi (ordine = dipendenze)</h3>
          <button type="button" onClick={addLineType} disabled={busy}>Aggiungi fase</button>
        </div>
        <div style={{ overflowX: "auto" }}>
          <table style={{ borderCollapse: "collapse", width: "100%" }}>
            <thead>
              <tr>
                <th style={{ textAlign: "left", padding: 6, borderBottom: "1px solid #ddd" }}>Fase</th>
                <th style={{ textAlign: "left", padding: 6, borderBottom: "1px solid #ddd" }}>Count</th>
                <th style={{ textAlign: "left", padding: 6, borderBottom: "1px solid #ddd" }}>Per ep</th>
                <th style={{ textAlign: "left", padding: 6, borderBottom: "1px solid #ddd" }}>Gg/ep (lav)</th>
                <th style={{ textAlign: "left", padding: 6, borderBottom: "1px solid #ddd" }}>Vincolante</th>
                <th style={{ textAlign: "left", padding: 6, borderBottom: "1px solid #ddd" }}></th>
              </tr>
            </thead>
            <tbody>
              {lineTypes.map((lt, idx) => (
                <tr key={`${lt.typeKey}_${idx}`}>
                  <td style={{ padding: 6, borderBottom: "1px solid #eee", minWidth: 220 }}>
                    {String(lt.typeKey || "").startsWith("custom_") ? (
                    <input
                      value={lt.label}
                      onChange={(e) => {
                        const label = e.target.value;
                        setLineTypes((prev) => prev.map((x, i) => (i === idx ? { ...x, label } : x)));
                      }}
                      style={{ width: "100%" }}
                    />
                  ) : (
                    <div style={{ padding: "6px 2px" }}>{lt.label}</div>
                  )}
                  </td>
                  <td style={{ padding: 6, borderBottom: "1px solid #eee" }}>
                    <input
                      type="number"
                      min={1}
                      value={lt.count}
                      onChange={(e) => {
                        const count = clampInt(e.target.value, 1, 999);
                        setLineTypes((prev) => prev.map((x, i) => (i === idx ? { ...x, count, perEpisode: Math.min(x.perEpisode, count) } : x)));
                      }}
                      style={{ width: 80 }}
                    />
                  </td>
                  <td style={{ padding: 6, borderBottom: "1px solid #eee" }}>
                    <input
                      type="number"
                      min={1}
                      value={lt.perEpisode}
                      onChange={(e) => {
                        const perEpisode = clampInt(e.target.value, 1, lt.count);
                        setLineTypes((prev) => prev.map((x, i) => (i === idx ? { ...x, perEpisode } : x)));
                      }}
                      style={{ width: 80 }}
                    />
                  </td>
                  <td style={{ padding: 6, borderBottom: "1px solid #eee" }}>
                    <input
                      type="number"
                      min={0}
                      value={lt.daysPerEpisode}
                      onChange={(e) => {
                        const daysPerEpisode = clampInt(e.target.value, 0, 999);
                        setLineTypes((prev) => prev.map((x, i) => (i === idx ? { ...x, daysPerEpisode } : x)));
                      }}
                      style={{ width: 90 }}
                    />
                  </td>
                  <td style={{ padding: 6, borderBottom: "1px solid #eee" }}>
                    <input
                      type="checkbox"
                      checked={!!lt.blocking}
                      onChange={() => setLineTypes((prev) => prev.map((x, i) => (i === idx ? { ...x, blocking: !x.blocking } : x)))}
                    />
                  </td>
                  <td style={{ padding: 6, borderBottom: "1px solid #eee", whiteSpace: "nowrap" }}>
                    <button type="button" onClick={() => moveLineType(idx, -1)} disabled={busy || idx === 0}>
                      ↑
                    </button>{" "}
                    <button type="button" onClick={() => moveLineType(idx, 1)} disabled={busy || idx === lineTypes.length - 1}>
                      ↓
                    </button>{" "}
                    <button type="button" onClick={() => deleteLineType(idx)} disabled={busy || lineTypes.length <= 1}>
                      Elimina
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div style={{ marginTop: 8, fontSize: 12, color: "#666" }}>
          <div><b>Vincolante</b>: se attivo, la fase successiva dello stesso episodio parte dopo la fine della fase precedente.</div>
          <div>Autoshift è trasversale perché le dipendenze sono per-episodio, non per calendario globale.</div>
          <div style={{ marginTop: 6 }}>
            Nota: al momento le "Visioni" sono fasi separate (Autore/Curatore/Rete) perché la timeline ragiona per fase. In seguito possiamo raggrupparle visivamente su una sola riga.
          </div>
        </div>
      </div>

      {/* bottom action bar */}
      <div
        style={{
          position: "sticky",
          bottom: 0,
          marginTop: 14,
          padding: "12px 0",
          background: "#fff",
          borderTop: "1px solid #eee",
          display: "flex",
          justifyContent: "flex-end",
          gap: 8,
        }}
      >
        <button onClick={() => nav(`/postplans/${planId}`)} disabled={busy}>
          Annulla
        </button>
        <button onClick={submit} disabled={busy} style={{ fontWeight: 800 }}>
          {busy ? "Genero..." : "Genera piano"}
        </button>
      </div>
    </div>
  );
}
